import type { AppId, AppSuiteApp } from '../../types/appSuites'
import { matchPath } from './matchPath'
import { isStorePath } from './storePaths'

function appScore(app: AppSuiteApp, pathname: string, pathById: Record<string, string>): number {
  let best = 0
  for (const section of app.sections) {
    for (const itemId of section.item_ids) {
      const path = pathById[itemId]
      if (path) best = Math.max(best, matchPath(pathname, path))
    }
  }
  for (const prefix of app.extra_route_prefixes) {
    best = Math.max(best, matchPath(pathname, prefix))
  }
  return best
}

function fallbackApp(
  apps: AppSuiteApp[],
  stickyId: AppId | null,
  suggestedId: AppId | null,
  accessibleIds: AppId[],
): AppId | null {
  const enabled = new Set(apps.filter((app) => app.enabled).map((app) => app.id))
  const accessible = new Set(accessibleIds)
  const candidates = [stickyId, suggestedId, accessibleIds[0] ?? null]
  for (const candidate of candidates) {
    if (candidate && enabled.has(candidate) && accessible.has(candidate)) return candidate
  }
  return null
}

/**
 * Which app owns this URL. A shared path stays in the app the user came from.
 * Routes outside every app keep the sticky app, then the suggestion, then the first accessible app.
 */
export function resolveActiveApp(
  pathname: string,
  apps: AppSuiteApp[],
  stickyId: AppId | null,
  suggestedId: AppId | null,
  accessibleIds: AppId[],
  pathById: Record<string, string>,
): AppId | null {
  if (pathname === '/' || isStorePath(pathname)) return null
  const enabled = apps.filter((app) => app.enabled)
  let bestScore = 0
  let bestOrder = Number.POSITIVE_INFINITY
  let bestId: AppId | null = null
  let stickyScore = 0
  for (const app of enabled) {
    const score = appScore(app, pathname, pathById)
    if (app.id === stickyId) stickyScore = score
    const wins = score > bestScore || (score === bestScore && score > 0 && app.order < bestOrder)
    if (wins) {
      bestScore = score
      bestOrder = app.order
      bestId = app.id
    }
  }
  if (bestScore > 0) {
    if (stickyId && stickyScore === bestScore) return stickyId
    return bestId
  }
  return fallbackApp(apps, stickyId, suggestedId, accessibleIds)
}
